import { GraphQLError } from "graphql";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getAccommodationProductsQuery,
  getBookingProductsQuery,
  getOccupantProductsQuery,
} from "rsl-api-106/lib/queries";
import {
  ProductByCategory,
  Query,
  QueryGetAccommodationProductsArgs,
  QueryGetBookingProductsArgs,
  QueryGetOccupantProductsArgs,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import { useCart, useSession } from "@hooks";
import { sortBy } from "@utils/arrays";
import { getCachedProducts, setCachedProducts } from "@utils/cache";

import ProductItem2 from "../ProductItem2/ProductItem2";
import SubProducts from "../SubProducts";

type ProductListProps = {
  mode: "occupant" | "stay" | "global";
  title?: JSX.Element;
  childContainerClass?: string;
  parentContainerClass?: string;
  emptyMessage?: string;
  hideIfEmpty?: boolean;
  occupantGuiid?: number;
  stayGuiid?: number;
  categoryCodes?: string[];
  cartItemID?: number;
  loadingChildren?: React.ReactNode;
  excludeCategories?: string[];
  showCriteria?: string | boolean;
};
const languagesVarsCodes = [
  "LANG_neobooking_products_adding_in_progress", //Ajout de la préstation à votre panier en cours
  "LANG_neobooking_products_removing_in_progress", //Suppression de la préstation de votre panier en cours
  "LANG_neobooking_products_added", //Préstation ajoutée
  "LANG_neobooking_products_deleted", //Préstation supprimée
  "LANG_neobooking_products_loading_in_progress", //Chargement des services en cours
  "LANG_neobooking_please_wait", //Veuillez patienter quelques instants
  "LANG_neobooking_see_more", //+ Voir plus
];

const SORT_CODE = "PSORTW";

const ProductList: React.FC<ProductListProps> = ({
  mode,
  title,
  childContainerClass,
  parentContainerClass,
  emptyMessage = "Aucun produit disponible",
  hideIfEmpty = true,
  occupantGuiid,
  stayGuiid,
  categoryCodes,
  cartItemID,
  loadingChildren,
  excludeCategories,
  showCriteria = false,
}) => {
  /* Hooks */
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue } = useSession();
  const { cart, addProductToOccupant, addProductToGlobal } = useCart();
  /* State */
  const [productsByCategory, setProductByCategory] = useState<
    ProductByCategory[]
  >([]);
  /* Loading State */
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingAddRemove, setIsLoadingAddRemove] = useState<boolean>(false);
  const [loadingTitle, setLoadingTitle] = useState<string>("");
  useEffect(() => {
    const abortController = new AbortController();
    if (sessionId) {
      //On load les produits aux dossier
      if (mode === "global") {
        const rdn = Math.floor(Math.random() * 4);
        getCachedProducts("global", rdn, (response) => {
          if (!response) {
            client
              .query<Query, QueryGetBookingProductsArgs>({
                query: getBookingProductsQuery(),
                variables: {
                  session: { name: sessionId },
                  input: {
                    categoryCodes: categoryCodes,
                  },
                },
                context: {
                  fetchOptions: {
                    signal: abortController.signal,
                  },
                },
                fetchPolicy: "no-cache",
              })
              .then((response) => {
                if (
                  response.data &&
                  response.data.getBookingProducts?.bookingProducts
                ) {
                  let products: ProductByCategory[] = [];
                  if (excludeCategories) {
                    response.data.getBookingProducts?.bookingProducts?.forEach(
                      (p) => {
                        if (excludeCategories.includes(p?.categoryCode!!)) {
                        } else {
                          products = [...products, p!!];
                        }
                      }
                    );
                  }

                  //On sort les produits Booking;
                  let sorted: ProductByCategory[] = [];
                  for (const category of products) {
                    sorted.push({
                      ...category,
                      products: sortBy(category?.products ?? [], (x) =>
                        parseInt(
                          x?.cmsCriterias?.find((y) => y?.code === SORT_CODE)
                            ?.value ?? "0"
                        )
                      ),
                    });
                  }

                  setProductByCategory(sorted as ProductByCategory[]);

                  setCachedProducts(
                    "global",
                    rdn,
                    JSON.stringify(
                      response.data.getBookingProducts?.bookingProducts
                    )
                  );
                }
              })
              .catch((err: GraphQLError) => {
                console.log(err);
              })
              .finally(() => {
                setIsLoading(false);
              });
          } else {
            //on a une réponse en cache,
            //on set la valeur qu'on a dedans
            response
              ?.json()
              .then((data) => {
                let productByCat = data as ProductByCategory[];
                let products: ProductByCategory[] = [];
                if (excludeCategories) {
                  productByCat.forEach((p) => {
                    if (excludeCategories.includes(p?.categoryCode!!)) {
                    } else {
                      products = [...products, p!!];
                    }
                  });
                }

                //On sort les produits Booking;
                let sorted: ProductByCategory[] = [];
                for (const category of products) {
                  sorted.push({
                    ...category,
                    products: sortBy(category?.products ?? [], (x) =>
                      parseInt(
                        x?.cmsCriterias?.find((y) => y?.code === SORT_CODE)
                          ?.value ?? "0"
                      )
                    ),
                  });
                }

                setProductByCategory(sorted as ProductByCategory[]);
              })
              .finally(() => {
                setIsLoading(false);
              });
          }
        });
      }
      //On load les produits pour l'occupant
      if (mode === "occupant" && occupantGuiid) {
        getCachedProducts("occupant", occupantGuiid, (response) => {
          if (!response) {
            client
              .query<Query, QueryGetOccupantProductsArgs>({
                query: getOccupantProductsQuery(),
                variables: {
                  session: { name: sessionId },
                  input: {
                    occupantGuiid,
                    categoryCodes: categoryCodes,
                  },
                },
                context: {
                  fetchOptions: {
                    signal: abortController.signal,
                  },
                },
                fetchPolicy: "no-cache",
              })
              .then((response) => {
                if (
                  response.data &&
                  response.data.getOccupantProducts?.occupantProducts
                ) {
                  let products: ProductByCategory[] = [];
                  if (excludeCategories) {
                    response.data.getOccupantProducts?.occupantProducts?.forEach(
                      (p) => {
                        if (excludeCategories.includes(p?.categoryCode!!)) {
                        } else {
                          products = [...products, p!!];
                        }
                      }
                    );
                  }

                  //On sort les produits Occupant;
                  let sorted: ProductByCategory[] = [];
                  for (const category of products) {
                    sorted.push({
                      ...category,
                      products: sortBy(category?.products ?? [], (x) =>
                        parseInt(
                          x?.cmsCriterias?.find((y) => y?.code === SORT_CODE)
                            ?.value ?? "0"
                        )
                      ),
                    });
                  }

                  setProductByCategory(sorted as ProductByCategory[]);

                  setCachedProducts(
                    "occupant",
                    occupantGuiid,
                    JSON.stringify(
                      response.data.getOccupantProducts?.occupantProducts
                    )
                  );
                }
              })
              .catch((err: GraphQLError) => {
                console.log(err);
              })
              .finally(() => {
                setIsLoading(false);
              });
          } else {
            //on a une réponse en cache,
            //on set la valeur qu'on a dedans
            response
              ?.json()
              .then((data) => {
                let productByCat = data as ProductByCategory[];
                let products: ProductByCategory[] = [];
                if (excludeCategories) {
                  productByCat.forEach((p) => {
                    if (excludeCategories.includes(p?.categoryCode!!)) {
                    } else {
                      products = [...products, p!!];
                    }
                  });
                }
                //On sort les produits Occupant;
                let sorted: ProductByCategory[] = [];
                for (const category of products) {
                  sorted.push({
                    ...category,
                    products: sortBy(category?.products ?? [], (x) =>
                      parseInt(
                        x?.cmsCriterias?.find((y) => y?.code === SORT_CODE)
                          ?.value ?? "0"
                      )
                    ),
                  });
                }

                setProductByCategory(sorted as ProductByCategory[]);
              })
              .finally(() => {
                setIsLoading(false);
              });
          }
        });
      }
      //On load les produits pour la chambre
      if (mode === "stay" && stayGuiid) {
        //on regarde dans le cache si pas de data
        getCachedProducts("stay", stayGuiid, (response) => {
          if (!response) {
            //si pas de response,
            //on execute la requête puis on met résultat en cache
            client
              .query<Query, QueryGetAccommodationProductsArgs>({
                query: getAccommodationProductsQuery(),
                variables: {
                  session: { name: sessionId },
                  input: {
                    stayGuiid,
                    categoryCodes: categoryCodes,
                  },
                },
                context: {
                  fetchOptions: {
                    signal: abortController.signal,
                  },
                },
                fetchPolicy: "no-cache",
              })
              .then((response) => {
                if (
                  response.data &&
                  response.data.getAccommodationProducts?.accommodationProducts
                ) {
                  let products: ProductByCategory[] = [];
                  if (excludeCategories) {
                    response.data.getAccommodationProducts?.accommodationProducts.forEach(
                      (p) => {
                        if (excludeCategories.includes(p?.categoryCode!!)) {
                        } else {
                          products = [...products, p!!];
                        }
                      }
                    );
                  }
                  //On sort les produits Accommodations;
                  let sorted: ProductByCategory[] = [];
                  for (const category of products) {
                    sorted.push({
                      ...category,
                      products: sortBy(category?.products ?? [], (x) =>
                        parseInt(
                          x?.cmsCriterias?.find((y) => y?.code === SORT_CODE)
                            ?.value ?? "0"
                        )
                      ),
                    });
                  }

                  setProductByCategory(sorted as ProductByCategory[]);
                  setCachedProducts(
                    "stay",
                    stayGuiid,
                    JSON.stringify(
                      response.data.getAccommodationProducts
                        ?.accommodationProducts
                    )
                  );
                }
              })
              .catch((err: GraphQLError) => {
                console.log(err);
              })
              .finally(() => {
                setIsLoading(false);
              });
          } else {
            //on a une réponse en cache,
            //on set la valeur qu'on a dedans
            response
              ?.json()
              .then((data) => {
                let productByCat = data as ProductByCategory[];
                let products: ProductByCategory[] = [];
                if (excludeCategories) {
                  productByCat.forEach((p) => {
                    if (excludeCategories.includes(p?.categoryCode!!)) {
                    } else {
                      products = [...products, p!!];
                    }
                  });
                }
                //On sort les produits Accommodations;
                let sorted: ProductByCategory[] = [];
                for (const category of products) {
                  sorted.push({
                    ...category,
                    products: sortBy(category?.products ?? [], (x) =>
                      parseInt(
                        x?.cmsCriterias?.find((y) => y?.code === SORT_CODE)
                          ?.value ?? "0"
                      )
                    ),
                  });
                }

                setProductByCategory(sorted as ProductByCategory[]);
              })
              .finally(() => {
                setIsLoading(false);
              });
          }
        });
      }
    }
    return () => {
      abortController.abort();
    };
  }, [sessionId]);

  const isInLineItem = (productId: number) => {
    let checked = false;
    let quantity = 0;
    if (mode === "occupant" && occupantGuiid && stayGuiid && cartItemID) {
      const lineitem = cart?.reservations
        ?.find((x) => x?.cartItemID === cartItemID)
        ?.stays?.find((x) => x?.guiid === stayGuiid)
        ?.occupants?.find((x) => x?.guiid === occupantGuiid)
        ?.lineItems?.find((x) => x?.product?.id === productId);
      if (lineitem) {
        checked = true;
        quantity = lineitem.quantity ?? 0;
      }
    }
    if (mode === "stay" && stayGuiid && cartItemID) {
      const lineitemsStay = cart?.reservations
        ?.find((reservation) => reservation?.cartItemID === cartItemID)
        ?.stays?.find((stay) => stay?.guiid === stayGuiid);
      const allLineItems = lineitemsStay?.occupants?.map((x) => x?.lineItems);
      allLineItems?.forEach((items) => {
        items?.forEach((lineitem) => {
          if (lineitem?.product?.id === productId) {
            checked = true;
            quantity = lineitem.quantity ?? 0;
          }
        });
      });
    }
    if (mode === "global" && cartItemID) {
      const lineitemsGlobal = cart?.reservations?.find(
        (reservation) => reservation?.cartItemID === cartItemID
      );
      const allLineItems = lineitemsGlobal?.stays?.map(
        (stay) => stay?.occupants
      );
      allLineItems?.forEach((occupants) => {
        occupants?.forEach((occupant) => {
          let items = occupant?.lineItems;
          items?.forEach((lineitem) => {
            if (lineitem?.product?.id === productId) {
              checked = true;
              quantity = lineitem.quantity ?? 0;
            }
          });
        });
      });
    }
    return { checked, quantity };
  };
  const handleAddProduct = (
    productCode: string,
    productId: number,
    quantity: number,
    stayGuiid: number,
    callback: (err?: GraphQLError) => void
  ) => {
    setLoadingTitle(
      quantity === 0
        ? `${getLanguageVarValue(
            "LANG_neobooking_products_removing_in_progress"
          )}...`
        : `${getLanguageVarValue(
            "LANG_neobooking_products_adding_in_progress"
          )}...`
    );
    setIsLoadingAddRemove(true);

    addProductToGlobal(
      productCode,
      productId,
      quantity,
      (err) => {
        if (err) toast.error(err.message);
        else
          toast.success(
            `${
              quantity === 0
                ? getLanguageVarValue("LANG_neobooking_products_deleted")
                : getLanguageVarValue("LANG_neobooking_products_added")
            }`
          );
        setIsLoadingAddRemove(false);
        callback(err);
      },
      stayGuiid
    );
  };
  const handleAddOccupantProduct = (
    productCode: string,
    productId: number,
    occupantGuiid: number,
    quantity: number,
    callback: (err?: GraphQLError) => void
  ) => {
    setLoadingTitle(
      quantity === 0
        ? `${getLanguageVarValue(
            "LANG_neobooking_products_removing_in_progress"
          )}...`
        : `${getLanguageVarValue(
            "LANG_neobooking_products_adding_in_progress"
          )}...`
    );
    setIsLoadingAddRemove(true);

    addProductToOccupant(
      productCode,
      productId,
      occupantGuiid,
      quantity,
      (err) => {
        if (err) toast.error(err.message);
        else
          toast.success(
            `${
              quantity === 0
                ? getLanguageVarValue("LANG_neobooking_products_deleted")
                : getLanguageVarValue("LANG_neobooking_products_added")
            }`
          );
        callback(err);
        setIsLoadingAddRemove(false);
      }
    );
  };
  return (
    <>
      {isLoading ? (
        <div className={parentContainerClass!!}>
          <div className={childContainerClass!!}>
            <span className="block">{loadingChildren}</span>
          </div>
        </div>
      ) : productsByCategory.length > 0 ? (
        <div className={parentContainerClass!!}>
          {title && title}
          <div className={childContainerClass!!}>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {productsByCategory.map(
                (productByCategory, productByCategoryIndex) => {
                  return (
                    productByCategory && (
                      <div
                        className="tw-flex tw-flex-col tw-gap-4"
                        key={`productByCategory_${mode}_${
                          occupantGuiid
                            ? occupantGuiid
                            : stayGuiid
                            ? stayGuiid
                            : ""
                        }_${
                          productByCategory.categoryCode
                        }_${productByCategoryIndex}`}
                      >
                        {productByCategory?.products?.map(
                          (product, productIndex) => {
                            return product?.subProducts?.length!! > 0 ? (
                              <SubProducts
                                key={`subproductitem_${mode}_${
                                  occupantGuiid
                                    ? occupantGuiid
                                    : stayGuiid
                                    ? stayGuiid
                                    : ""
                                }_${product?.id}_${productIndex}`}
                                occupantGuiid={occupantGuiid}
                                stayGuiid={stayGuiid}
                                mode={mode}
                                products={product?.subProducts!!}
                                originalLabel={product?.originalLabel!!}
                                isInLineItem={isInLineItem}
                                addLoadingText={getLanguageVarValue(
                                  "LANG_neobooking_products_adding_in_progress"
                                )}
                                removeLoadingText={getLanguageVarValue(
                                  "LANG_neobooking_products_removing_in_progress"
                                )}
                                addedSuccessText={getLanguageVarValue(
                                  "LANG_neobooking_products_added"
                                )}
                                removedSuccessText={getLanguageVarValue(
                                  "LANG_neobooking_products_deleted"
                                )}
                              />
                            ) : (
                              product && (
                                <ProductItem2
                                  key={`productitem_${mode}_${
                                    occupantGuiid
                                      ? occupantGuiid
                                      : stayGuiid
                                      ? stayGuiid
                                      : ""
                                  }_${product?.id}_${productIndex}`}
                                  mode={mode}
                                  product={product}
                                  occupantGuiid={occupantGuiid}
                                  stayGuiid={stayGuiid}
                                  defaultChecked={
                                    isInLineItem(product.id).checked
                                  }
                                  defaultQuantity={
                                    isInLineItem(product.id).quantity
                                  }
                                  onAddProduct={handleAddProduct}
                                  onAddOccupantProduct={
                                    handleAddOccupantProduct
                                  }
                                  showCriteria={showCriteria}
                                />
                              )
                            );
                          }
                        )}
                      </div>
                    )
                  );
                }
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {hideIfEmpty && hideIfEmpty ? (
            <></>
          ) : (
            <div className={parentContainerClass!!}>
              <div className={childContainerClass!!}>
                {title && title}
                <span className="tw-block">{emptyMessage}</span>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};
ProductList.propTypes = {
  //
};
export default ProductList;
